import { Injectable } from '@angular/core';
import {
  Machine_,
  Cutter_,
  Material_,
  Speed_,
  Parameters_,
  Hem_,
  Machining_,
  Recommendation_,
  RecommendationLabel
} from './objects';
import _ from 'lodash';
import math from 'mathjs';
import { StandardFormulas } from './standardFormulas';
import { ConversionFormulas } from './conversionsFormulas';

@Injectable()
export class Formulas {
  public hem: Hem_;
  public standardTool: Cutter_;
  public standardMaterial: Material_;
  public ArrayMatlISO: string[];
  constructor(public conversionsFormulas: ConversionFormulas, public standardFormulas: StandardFormulas) {
    this.ArrayMatlISO = [
      'n1',
      'n2',
      'n3',
      'n4',
      'n5',
      'n6',
      'n7',
      'n8',
      'p1',
      'p2',
      'p3',
      'p4',
      'p5',
      'p6',
      'm1',
      'm2',
      'm3',
      'm4',
      'm5',
      'k1',
      'k2',
      'k3',
      's1',
      's2',
      's3',
      's4',
      's5',
      's6',
      's7'
    ];

    this.standardTool = this.getStandardTool();
    this.standardMaterial = this.getStandardMaterial();
    this.hem = this.getHEM();
  }

  public emRoundFactor = (endmillDia: number): number => {
    let emRoundFactor = math.round(math.log10(100 * endmillDia), 0) / 400;
    return emRoundFactor;
  };

  public calculateResultantFactor = (tool: Cutter_): number => {
    let cof = 0;
    if (tool.coatingCode) {
      switch (tool.coatingCode.toLowerCase()) {
        case 'uncoated':
          cof = 0.5;
          break;
        case 'un':
          cof = 0.5;
          break;
        case 'aplus':
        case 'zplus':
          cof = 0.4;
          break;
      }
    }
    let shearAngle = math.PI / 4 - tool.rake / 2 - math.atan(cof) / 2;
    if (shearAngle > 0) {
      let shearForce = 1 / math.sin(shearAngle);

      let normalForce = shearForce / math.cos(shearAngle - tool.rake);

      return normalForce * math.sqrt(1 + math.pow(cof, 2));
    } else {
      return 1;
    }
  };

  public calculateInchesPerMinutes = (speed: number, fpt: number, fluteCount: number): number => {
    return speed * fluteCount * fpt;
  };

  public calculateMaterialStretch = (
    material: Material_,
    standardMaterial: Material_ = this.standardMaterial,
    hem: Hem_ = this.hem
  ): number => {
    return (
      math.pow(material.plasticity / this.standardMaterial.plasticity, hem.plasticExp) *
      math.pow(this.standardMaterial.elasticity / material.elasticity, hem.elasticExp)
    );
  };

  public calculateMaterialChip = (material: Material_, tool: Cutter_, hardness: number = 0): number => {
    hardness = hardness === 0 ? material.hra : hardness;
    let emUTS = 372;
    let tensileStrength =
      (material.tensileStrength * this.conversionsFormulas.convertHRAtoUTS(hardness)) /
      this.conversionsFormulas.convertHRAtoUTS(material.hra);
    let shearStrength = tensileStrength * material.shearFactor;

    return (emUTS * math.PI) / (128 * this.calculateResultantFactor(tool) * shearStrength * math.cos(tool.helix));
  };

  public calculateMaterialSurfaceFeetPerMinute = (
    material: Material_,
    hardness: number = 0,
    standardMaterial: Material_ = this.standardMaterial,
    hem: Hem_ = this.hem
  ): number => {
    hardness = hardness === 0 ? material.hra : hardness;

    let tensileStrength =
      (material.tensileStrength * this.conversionsFormulas.convertHRAtoUTS(hardness)) /
      this.conversionsFormulas.convertHRAtoUTS(material.hra);
    let hardInflection = this.conversionsFormulas.convertHBWtoHRA(392);
    let hardPow = 184.0 / 9.0;
    let hardMax = 92.0;
    let hardExp = math.pow(hardness / hardInflection, hardPow);
    let hardFactor = 1 - ((1 + math.pow(hardInflection / hardMax, hardPow)) * hardExp) / (1 + hardExp);
    let hardLimit = 71.5;
    let densityFactor = math.pow(material.density, 1.0 / 3.0);
    let cteFactor = math.pow(material.thermalExpansionCoefficient, 1.0 / 2.0);
    let abrasiveFactor = math.pow(this.conversionsFormulas.convertHRAtoUTS(hardness) / tensileStrength, 11.0 / 90.0);
    let thermalFactor = math.pow(material.thermal, 23.0 / 30.0);
    let heatCapFactor = math.pow(material.heat, 15.0 / 8.0);
    let cFactor =
      (194.8 * math.pow(this.standardMaterial.elasticity, hem.elasticExp)) /
      math.pow(this.standardMaterial.plasticity, hem.plasticExp);

    if (hardness > hardLimit) {
      hardFactor = hardFactor * (1 + math.pow((hardness - hardLimit) / 7.5, 2.0));
    }

    let msfpm = (cFactor * hardFactor * thermalFactor * heatCapFactor) / (densityFactor * abrasiveFactor * cteFactor);

    return msfpm;
  };

  public calculateChipThinness = (cutWidth: number, endmillDiameter: number) => {
    return endmillDiameter / 2 > cutWidth && cutWidth > 0
      ? (2 * math.sqrt(endmillDiameter * cutWidth - math.pow(cutWidth, 2))) / endmillDiameter
      : 1;
  };

  public calculateChipCoreRatio = (tool: Cutter_, stickOut: number, axDepth: number): number => {
    let dia2Core =
      (1 - 0.62 * math.sqrt(2 / math.sqrt(3))) / (math.sqrt(2 / math.sqrt(3)) - 1) +
      parseInt((tool.fluteCount - 5) / 2 + ''); //cause its expecting a string
    let holderStress =
      (this.standardFormulas.calculateBendingMoment(stickOut, axDepth, 1) * tool.cutterDiameter) /
      (2 * this.standardFormulas.calculateMomentOfInertia(tool.cutterDiameter));
    let locStress =
      (this.standardFormulas.calculateBendingMoment(tool.lengthOfCut, axDepth, 1) * tool.cutterDiameter) /
      (2 *
        this.standardFormulas.calculateMomentOfInertia(
          (tool.coreDiameter + dia2Core * tool.cutterDiameter) / (1 + dia2Core)
        ));

    return holderStress / locStress;
  };

  public calculateChipWorkFactor = (toolDiameter: number, stickOut: number, axDepth: number): number => {
    let chipExp = 3.5;
    let chipBase = math.pow(toolDiameter + 1, chipExp);
    let chipDiaRatio = (chipBase - 1) / (toolDiameter * (chipBase + math.pow(1.5, chipExp) - 2));
    let emStress = this.standardFormulas.calculateStressFactor(toolDiameter, stickOut, axDepth);

    return chipDiaRatio / emStress;
  };

  public calculateScallopStepOver = (emDiameter: number, roughness: number): number => {
    let emRadius = emDiameter / 2;
    let scallopHeight = roughness / 1000000;

    return 2 * math.sqrt(math.pow(emRadius, 2) - math.pow(emRadius - scallopHeight, 2));
  };

  public calculateScallopHeight = (emDiameter: number, stepOver: number): number => {
    let emRadius = emDiameter / 2;
    let scallopHeight = emRadius - math.sqrt(math.pow(emRadius, 2) - math.pow(stepOver / 2, 2));

    return 1000000 * scallopHeight;
  };

  public calculateCutDepth = (tool: Cutter_, hardness: number, path: string = 'High Efficiency Milling'): number => {
    if (path === 'Trochoidal Slotting' || path === 'High Efficiency Milling' || path === 'Finishing') {
      return tool.lengthOfCut;
    } else if (path === '3D Surfacing') {
      const roundFactor = math.round(math.log10(100 * tool.cutterDiameter), 0) / 200;
      return roundFactor * math.floor(tool.cutterDiameter / (15 * roundFactor));
    } else if (hardness > 0) {
      const fluteFactor = path === 'Full Slotting' ? tool.fluteCount + 2 : tool.fluteCount + 3;
      const hardExp = path === 'Full Slotting' ? 1.0 / 2.0 : 3.0 / 20.0;
      const cFactor = path === 'Full Slotting' ? 3068 : 1293;
      const stepDown = cFactor / (fluteFactor * math.pow(hardness, hardExp));
      const cutDepth = (math.round(stepDown / 5) * tool.cutterDiameter) / 20;
      return Math.min(cutDepth, tool.lengthOfCut);
    } else {
      return 0;
    }
  };

  calculateFinishWallStock = (diameter: number): number => {
    let roundFactor = math.round(math.log10(130 * diameter), 0) / 400;
    let stepPercent = math.pow(1 / diameter, 0.25) / 30;

    return diameter === 0 ? 0 : roundFactor * math.ceil((diameter * stepPercent) / roundFactor);
  };

  calculateEffectiveDiameter = (endmillDiameter: number, cutDepth: number): number => {
    return cutDepth < endmillDiameter / 2 ? 2 * math.sqrt(cutDepth * (endmillDiameter - cutDepth)) : endmillDiameter;
  };

  calculatePitch2Ramp = (pitch: number, pathDia: number): number => {
    let pathLength = math.PI * pathDia;
    return pathLength > 0 ? math.atan(pitch / pathLength) : 0;
  };

  calculateRamp2Pitch = (rampAng: number, pathDia: number): number => {
    let pathLength = math.PI * pathDia;
    return pathLength * math.tan(rampAng);
  };

  calculateMaxFilletRdoc = (endmillDia: number, filletRad: number, engageAng: number): number => {
    let emRad = endmillDia / 2;
    let pathRad = filletRad - emRad;
    let angRad = (math.PI * engageAng) / 180;

    return (
      filletRad -
      math.sqrt(math.pow(emRad, 2) + math.pow(pathRad, 2) - 2 * pathRad * emRad * math.cos(math.PI - angRad))
    );
  };

  calculateMinFilletRad = (endmillDia: number, cutWidth: number, engageAng: number): number => {
    let emRad = endmillDia / 2;
    let angRad = (math.PI * engageAng) / 180;
    let cosValue = 1 + math.cos(math.PI - angRad);

    return (2 * math.pow(emRad, 2) * cosValue - math.pow(cutWidth, 2)) / (2 * (emRad * cosValue - cutWidth));
  };

  calculateMaxFilletEmDia = (filletRad: number, cutWidth: number, engageAng: number): number => {
    let angRad = (math.PI * engageAng) / 180;
    let cosValue = 1 + math.cos(math.PI - angRad);
    let sqrtValue =
      cosValue * (cosValue * math.pow(filletRad, 2) - 4 * filletRad * cutWidth + 2 * math.pow(cutWidth, 2));

    return sqrtValue < 0 ? 0 : (cosValue * filletRad + math.sqrt(sqrtValue)) / cosValue;
  };

  calculateChipThin = (cutWidth: number, endmillDia: number): number => {
    return endmillDia / 2 > cutWidth && cutWidth > 0
      ? (2 * math.sqrt(endmillDia * cutWidth - math.pow(cutWidth, 2))) / endmillDia
      : 1;
  };

  calculateMaxEngageAngle = (tool: Cutter_, cutDepth: number, workMatl: Material_, hardness: number): number => {
    let adocFactor =
      cutDepth > 0
        ? math.pow((36 * tool.cutterDiameter + 17 * (cutDepth - 2 * tool.cutterDiameter)) / (18 * cutDepth), 22.0 / 3.0)
        : 0;

    if (tool.breaks === true && cutDepth > tool.cutterDiameter) {
      cutDepth = tool.cutterDiameter * math.pow(cutDepth / tool.cutterDiameter, 1.0 / 3.0);
    }

    let chipRatio =
      (this.calculateMaterialChip(workMatl, this.standardTool, hardness) * this.calculateMaterialStretch(workMatl)) /
      this.calculateMaterialChip(this.standardMaterial, this.standardTool, this.standardMaterial.hra);

    let sfmRatio =
      this.calculateMaterialSurfaceFeetPerMinute(workMatl, hardness) /
      (this.calculateMaterialStretch(workMatl) * this.calculateMaterialSurfaceFeetPerMinute(this.standardMaterial));

    let helixRatio = (1.5 + math.sin(tool.helix)) / (1.5 + math.sin(this.standardTool.helix)); // this require a workMatl and workTool, might have to do further investigation where the "WORK" is set
    let fluteArea =
      (math.pow(tool.cutterDiameter, 2) - math.pow(tool.coreDiameter, 2)) /
      (4 * tool.fluteCount * math.pow(tool.cutterDiameter, 2));
    fluteArea =
      (fluteArea * this.standardTool.fluteCount) /
      (math.pow(this.standardTool.cutterDiameter, 2) - math.pow(this.standardTool.coreDiameter, 2));

    if (cutDepth > 0 && tool.fluteCount > 0) {
      let maxAngle =
        (56 * adocFactor * helixRatio * math.pow(fluteArea, 3.0 / 4.0) * math.pow(sfmRatio, 1.0 / 12.0)) /
        math.pow(chipRatio, 3.0 / 19.0);

      return maxAngle > 180 ? 180 : maxAngle;
    } else {
      return 0;
    }
  };

  setCutWidth = (tool: Cutter_, cutDepth: number, workMatl: Material_, path: string, hardness: number = 0): number => {
    if (hardness == 0) {
      hardness = workMatl.hra;
    }

    if (path === 'Trochoidal Slotting') {
      path = 'High Efficiency Milling';
    }

    let baseSFM =
      this.calculateMaterialSurfaceFeetPerMinute(workMatl, hardness) / this.calculateMaterialStretch(workMatl);

    let maxEngageAng = this.calculateMaxEngageAngle(tool, cutDepth, workMatl, hardness);
    let engageAng = 0;
    let adocFactor =
      cutDepth > 0
        ? math.pow((36 * tool.cutterDiameter + 17 * (cutDepth - 2 * tool.cutterDiameter)) / (18 * cutDepth), 22.0 / 3.0)
        : 0;

    if (tool.breaks === true && cutDepth > tool.cutterDiameter) {
      cutDepth = tool.cutterDiameter * math.pow(cutDepth / tool.cutterDiameter, 1.0 / 4.0);
    }

    if (path === 'High Efficiency Milling') {
      let sfmRatio = baseSFM / this.calculateMaterialSurfaceFeetPerMinute(this.standardMaterial);
      let chipRatio =
        (this.calculateMaterialChip(workMatl, this.standardTool, hardness) * this.calculateMaterialStretch(workMatl)) /
        this.calculateMaterialChip(this.standardMaterial, this.standardTool, this.standardMaterial.hra);
      let fluteArea =
        (math.pow(tool.cutterDiameter, 2) - math.pow(tool.coreDiameter, 2)) /
        (4 * tool.fluteCount * math.pow(tool.cutterDiameter, 2));
      fluteArea =
        (fluteArea * this.standardTool.fluteCount) /
        (math.pow(this.standardTool.cutterDiameter, 2) - math.pow(this.standardTool.coreDiameter, 2));

      engageAng =
        (41 * adocFactor * math.pow(fluteArea, 2.0 / 5.0) * math.pow(sfmRatio, 1.0 / 5.0)) /
        math.pow(chipRatio, 1.0 / 12.0);
    } else {
      let widthFactor = 50.0 / 163.0;
      let fluteFactor = 1.0 / 2.0;
      let sfmExp = 8.0 / 45.0;

      engageAng =
        (180 * widthFactor * adocFactor * math.pow(baseSFM, sfmExp)) /
        (math.PI * math.atan(tool.fluteCount * fluteFactor));
    }

    if (engageAng >= maxEngageAng) {
      engageAng = maxEngageAng;
    }

    let fws = this.calculateFinishWallStock(tool.cutterDiameter);
    let ttr = this.conversionsFormulas.convertTeaToRdoc(engageAng, tool.cutterDiameter);

    return path === 'Finishing' ? fws : ttr;
  };

  // machine Formulas

  public calculateStandardMachining = (
    material: Material_,
    tool: Cutter_,
    millingParameters: Parameters_,
    hardness: number,
    standardMaterial: Material_ = this.standardMaterial,
    hem: Hem_ = this.hem,
    standardTool: Cutter_ = this.standardTool
  ): Machining_ => {
    let cutWidth = millingParameters.cutWidth;
    let toolPath = millingParameters.toolPath.operationName;

    if (toolPath === 'Chamfering') {
      cutWidth = millingParameters.chamferWidth;
    }

    if (toolPath === 'Trochoidal Slotting') {
      toolPath = 'High Efficiency Milling';
    }

    let machining: Machining_ = new Machining_();
    machining.chipLoad = 0;

    if (hardness === 0) {
      hardness = material.hra;
    }

    let startSFM = this.calculateMaterialSurfaceFeetPerMinute(material, hardness);
    let unitSFM = startSFM / this.calculateMaterialSurfaceFeetPerMinute(this.configureMaterial(standardMaterial));

    if (tool.pitch == 'constant' || tool.pitch == 'even') {
      startSFM = (6 * startSFM) / 7;
    }

    machining.feedFactor = 1 / this.calculateChipThinness(cutWidth, tool.cutterDiameter);

    let coreRatio = this.calculateChipCoreRatio(tool, millingParameters.stickOut, millingParameters.cutDepth);
    let chipWorkAdjust = this.calculateChipWorkFactor(
      tool.cutterDiameter,
      millingParameters.stickOut,
      millingParameters.cutDepth
    );
    let maxChipAdjust = 2.5 * math.tan(math.PI / 4 - tool.rake) * math.cos(tool.helix);
    let chipThickness = hem.emSafety * tool.cutterDiameter * this.calculateMaterialChip(material, tool, hardness);
    let helixRatio = math.cos(tool.helix) / math.cos(this.standardTool.helix);

    let axialFactor = math.pow(
      (3 *
        tool.cutterDiameter *
        math.pow(
          (313 * math.pow(tool.cutterDiameter, 3)) /
            (20 * math.pow(millingParameters.cutDepth, 3) -
              50 * millingParameters.cutDepth * math.pow(millingParameters.stickOut, 2) +
              39 * math.pow(millingParameters.stickOut, 3)),
          8.0 / 7.0
        )) /
        millingParameters.stickOut,
      1.0 / 3.0
    );
    let emUTSRatio = 1;
    let rampRatio = 1;
    let coreSpeedRatio = 1;

    if (tool.end.indexOf('Chamfer') > -1) {
      coreRatio = 1;
    }

    if (coreRatio > 1) {
      coreSpeedRatio = math.pow(coreRatio, 2.0 / (3.0 * coreRatio));
      coreRatio = 1;
    }

    if (toolPath == 'High Efficiency Milling') {
      if (chipWorkAdjust > maxChipAdjust) {
        chipWorkAdjust = maxChipAdjust;
      }

      if (axialFactor > 1.5) {
        axialFactor = 1.5;
      }

      emUTSRatio = helixRatio * chipWorkAdjust * this.calculateMaterialStretch(material);
    } else if (toolPath === '3D Surfacing') {
      startSFM = 3000 * math.pow(unitSFM, 0.7);
      emUTSRatio = (helixRatio * math.sqrt(chipWorkAdjust) * this.calculateMaterialStretch(material)) / 1.4;
    } else if (toolPath === 'Chamfering') {
      startSFM = 133.45 * math.pow(unitSFM / math.pow(this.calculateMaterialStretch(material), 2.55), 0.7382);
      axialFactor = 1;
      emUTSRatio =
        (this.calculateChipThinness(cutWidth, tool.cutterDiameter) * tool.cutterDiameter) /
        (5.4 * cutWidth * math.pow(this.calculateMaterialStretch(material), 2.55));
    } else if (toolPath === 'Helical Plunge') {
      startSFM = (16 * math.pow(startSFM, 7.0 / 8.0)) / 25;
      chipWorkAdjust = this.calculateChipWorkFactor(
        tool.cutterDiameter,
        millingParameters.stickOut,
        math.sqrt(millingParameters.cutDepth)
      );
      axialFactor =
        ((1.25 + math.pow((2.0 * tool.cutterDiameter) / millingParameters.cutDepth, 1.0 / 8.0)) *
          math.pow((3.0 * tool.cutterDiameter) / millingParameters.stickOut, 2.0 / 3.0)) /
        2;

      let pathDiameter = millingParameters.cutWidth - tool.cutterDiameter;
      let helixLength = math.sqrt(math.pow(math.PI * pathDiameter, 2) + math.pow(millingParameters.pitch, 2));
      let pitchFactor = (math.PI * pathDiameter) / (48 * millingParameters.pitch);
      let rampRatio = 1 + math.pow(millingParameters.pitch / helixLength, 1.0 / 2.5);

      chipThickness = chipThickness * this.calculateMaterialStretch(material) * pitchFactor * rampRatio;
    } else {
      let unitChipExp = 1.0 / 3.0;

      if (toolPath === 'Finishing') {
        unitChipExp = 2.0 / 3.0;
        axialFactor = 1;
        coreSpeedRatio = 1;
        coreRatio = 1;
        chipThickness = chipThickness / (50 * tool.cutterDiameter);
      } else {
        coreSpeedRatio = math.pow(coreSpeedRatio, 1.0 / 3.0);
        coreRatio = math.pow(coreRatio, 1.0 / 3.0);
        axialFactor = math.pow(chipWorkAdjust, 1.0 / 3.0);
        chipThickness = 2.5 * chipThickness * this.calculateChipThinness(cutWidth, tool.cutterDiameter);
      }
      chipThickness =
        chipThickness *
        math.pow(
          this.calculateMaterialChip(standardMaterial, standardTool) /
            this.calculateMaterialChip(material, standardTool, hardness),
          unitChipExp
        );

      if (cutWidth > tool.cutterDiameter / 2) {
        chipThickness = (chipThickness * tool.cutterDiameter) / (2 * cutWidth);
        cutWidth = this.conversionsFormulas.convertTeaToRdoc(180, tool.cutterDiameter);
      } else if (toolPath !== 'Finishing') {
        cutWidth = this.conversionsFormulas.convertTeaToRdoc(
          90 + this.conversionsFormulas.convertRdocToTea(cutWidth, tool.cutterDiameter),
          tool.cutterDiameter
        );
      }

      let standardSFM = 325 / this.calculateChipThinness(cutWidth, tool.cutterDiameter);
      startSFM =
        standardSFM * math.pow(unitSFM * math.pow(this.calculateMaterialStretch(material), 8.0 / 13.0), 0.70515);
      emUTSRatio = (axialFactor * coreSpeedRatio * tool.cutterDiameter) / (2 * cutWidth);
    }

    machining.chipLoad = chipThickness * emUTSRatio * coreRatio;
    let workFactor =
      (2 * cutWidth * emUTSRatio) / (tool.cutterDiameter * this.calculateChipThinness(cutWidth, tool.cutterDiameter));

    if (toolPath === 'Helical Plunging') {
      machining.chipLoad = chipThickness * chipWorkAdjust * coreRatio;
      workFactor =
        (this.calculateChipWorkFactor(
          tool.cutterDiameter,
          millingParameters.stickOut,
          math.pow(millingParameters.cutDepth, 1.0 / 4.0)
        ) *
          rampRatio) /
        6;
    }

    machining.surfaceSpeed = (coreSpeedRatio * startSFM * axialFactor) / workFactor;

    if (tool.end.indexOf('Chamfer') > -1) {
      machining.baseMRR = 0;
    } else {
      machining.baseMRR = this.calculateMachiningMRR(material, tool, workFactor, hardness);
    }

    return machining;
  };

  public calculatePlungingMachining = (
    material: Material_,
    tool: Cutter_,
    plungingParameters: Parameters_,
    hardness: number,
    standardMaterial: Material_ = this.standardMaterial,
    hem: Hem_ = this.hem,
    standardTool: Cutter_ = this.standardTool
  ): Machining_ => {
    let holeDiameter: number = plungingParameters.holeDiameter;

    let machining: Machining_ = new Machining_();
    machining.chipLoad = 0;

    if (hardness === 0) {
      hardness = material.hra;
    }

    let startSFM = this.calculateMaterialSurfaceFeetPerMinute(material, hardness, standardMaterial, hem);

    if (tool.pitch == 'constant' || tool.pitch == 'even') {
      startSFM = (6 * startSFM) / 7;
    }

    machining.feedFactor = 1 / this.calculateChipThinness(holeDiameter, tool.cutterDiameter);

    let coreRatio = this.calculateChipCoreRatio(tool, plungingParameters.stickOut, plungingParameters.holeDepth);
    let chipWorkAdjust = this.calculateChipWorkFactor(
      tool.cutterDiameter,
      plungingParameters.stickOut,
      plungingParameters.holeDepth
    );
    let chipThickness = hem.emSafety * tool.cutterDiameter * this.calculateMaterialChip(material, tool, hardness);
    let axialFactor = math.pow(
      (3 *
        tool.cutterDiameter *
        math.pow(
          (313 * math.pow(tool.cutterDiameter, 3)) /
            (20 * math.pow(plungingParameters.holeDepth, 3) -
              50 * plungingParameters.holeDepth * math.pow(plungingParameters.stickOut, 2) +
              39 * math.pow(plungingParameters.stickOut, 3)),
          8.0 / 7.0
        )) /
        plungingParameters.stickOut,
      1.0 / 3.0
    );
    let rampRatio = 1;
    let coreSpeedRatio = 1;

    if (coreRatio > 1) {
      coreSpeedRatio = math.pow(coreRatio, 2.0 / (3.0 * coreRatio));
      coreRatio = 1;
    }

    if (coreRatio > 1) {
      coreSpeedRatio = math.pow(coreRatio, 2.0 / (3.0 * coreRatio));
      coreRatio = 1;
    }

    startSFM = (16 * math.pow(startSFM, 7.0 / 8.0)) / 25;
    chipWorkAdjust = this.calculateChipWorkFactor(
      tool.cutterDiameter,
      plungingParameters.stickOut,
      math.sqrt(plungingParameters.holeDepth)
    );
    axialFactor =
      ((1.25 + math.pow((2.0 * tool.cutterDiameter) / plungingParameters.holeDepth, 1.0 / 8.0)) *
        math.pow((3.0 * tool.cutterDiameter) / plungingParameters.stickOut, 2.0 / 3.0)) /
      2;

    let pathDiameter = holeDiameter - tool.cutterDiameter;
    let helixLength = math.sqrt(math.pow(math.PI * pathDiameter, 2) + math.pow(plungingParameters.pitch, 2));
    let pitchFactor = (math.PI * pathDiameter) / (48 * plungingParameters.pitch);
    rampRatio = 1 + math.pow(plungingParameters.pitch / helixLength, 1.0 / 2.5);

    chipThickness = chipThickness * this.calculateMaterialStretch(material) * pitchFactor * rampRatio;

    let workFactor =
      (this.calculateChipWorkFactor(
        tool.cutterDiameter,
        plungingParameters.stickOut,
        math.pow(plungingParameters.holeDepth, 1.0 / 4.0)
      ) *
        rampRatio) /
      6;

    machining.surfaceSpeed = (coreSpeedRatio * startSFM * axialFactor) / workFactor;
    machining.chipLoad = chipThickness * chipWorkAdjust * coreRatio;
    machining.baseMRR = this.calculateMachiningMRR(material, tool, workFactor, hardness);

    return machining;
  };

  public calculateMachiningMRR = (
    material: Material_,
    tool: Cutter_,
    workFactor: number,
    hardness: number,
    standardMaterial: Material_ = this.standardMaterial,
    standardTool: Cutter_ = this.standardTool,
    hem: Hem_ = this.hem
  ): number => {
    let newCutter: Cutter_ = {
      description: standardTool.description,
      fluteCount: standardTool.fluteCount,
      cutterDiameter: tool.cutterDiameter,
      lengthOfCut: 2.5 * tool.cutterDiameter,
      oal: 6 * tool.cutterDiameter
    };

    let mrrTool = this.constEndmillFeatures(newCutter);
    let baseSFM =
      this.calculateMaterialSurfaceFeetPerMinute(material, hardness) / this.calculateMaterialStretch(material);
    let baseIPM = this.conversionsFormulas.convertIptToIpm(
      this.conversionsFormulas.convertSfmToRpm(baseSFM, mrrTool.cutterDiameter),
      hem.emSafety * mrrTool.cutterDiameter * this.calculateMaterialChip(material, mrrTool, hardness),
      mrrTool.fluteCount
    );

    let baseMRR = baseIPM * math.pow(mrrTool.cutterDiameter, 2);

    return baseMRR;
  };

  public calculateMachining = (
    material: Material_,
    tool: Cutter_,
    parameters: Parameters_,
    hardness: number,
    standardMaterial: Material_ = this.standardMaterial,
    hem: Hem_ = this.hem,
    standardTool: Cutter_ = this.standardTool
  ): Machining_ => {
    return parameters.toolPath.operationName == 'Plunging'
      ? this.calculatePlungingMachining(material, tool, parameters, hardness, standardMaterial, hem, standardTool)
      : this.calculateStandardMachining(material, tool, parameters, hardness, standardMaterial, hem, standardTool);
  };

  //milling formulas
  public getStandardMaterial = (): Material_ => {
    let newMaterial: Material_;

    newMaterial = {
      iso: '',
      uns: '',
      group: '',
      subGroup: '',
      name: '',
      condition: '',
      description: '',
      density: 7.85,
      hardType: 'HBW',
      hardness: 302,
      tensileStrength: 1020,
      yields: 655,
      elongation: 0.18,
      elasticity: 205,
      poissons: 0.29,
      thermalExpansionCoefficient: 12.2,
      heat: 0.475,
      thermal: 42.6
    };

    return this.configureMaterial(newMaterial);
  };

  public getHEM = (): Hem_ => {
    let hem: Hem_ = {
      emSafety: 0.6,
      plasticExp: 1.0 / 2.0,
      elasticExp: 1.0 / 5.0
    };
    return hem;
  };

  public getStandardTool = (): Cutter_ => {
    let newCutter: Cutter_ = {
      edp: '00000',
      description: 'HEMV-R-50500',
      coatingCode: '',
      fluteCount: 5,
      cutterDiameter: 0.5,
      lengthOfCut: 1.25,
      oal: 3
    };

    return this.constEndmillFeatures(newCutter);
  };

  public calculateMachineFactor = (millValue: string, material: Material_, machine: Machine_): number => {
    let fixtureFactor = math.pow(
      machine.fixtureBar / 100,
      math.pow(100 / (machine.fixtureBar + (machine.fixtureBar - 35) / 6), 2)
    );
    let holderFactor = 1;

    if (machine.holder === 'Hydraulic' || machine.holder === 'Shrink-Fit') {
      holderFactor = 1.1;
    } else if (machine.holder === 'TG Collet' || machine.holder === 'DA Collet' || machine.holder === 'ER Collet') {
      holderFactor = 1.05;
    } else if (machine.holder === 'Milling Chuck') {
      holderFactor = 0.9;
    }

    if (millValue === 'SPEED') {
      return holderFactor * math.pow(1 / fixtureFactor, 1.0 / 8.0);
    }

    let spindleTaper = machine.spindle.replace(/CV/g, '');
    spindleTaper = spindleTaper.replace(/BT/g, '');
    spindleTaper = spindleTaper.replace(/HSK/g, '');
    spindleTaper = spindleTaper.replace(/NMTB/g, '');

    let spindleValue = parseFloat(spindleTaper);

    if (machine.holder === 'Hydraulic' || machine.holder === 'Shrink-Fit' || machine.holder === 'Milling Chuck') {
      holderFactor = 1.05;
    } else if (machine.holder === 'Weldon' || machine.holder === 'Press-Fit') {
      holderFactor = 1;
    } else if (machine.holder === 'TG Collet' || machine.holder === 'DA Collet' || machine.holder === 'ER Collet') {
      holderFactor = 0.95;
    }

    let spindleFactor = 1;

    if (machine.spindle.substring(0, 3) == 'HSK') {
      spindleFactor = 1 + math.log10(math.pow(spindleValue / 40, 1.0 / 12.0));
    } else if (machine.spindle.substring(0, 4) == 'NMTB') {
      spindleFactor = 0.95 + math.log10(math.pow(spindleValue / 40, 1.0 / 2.5));
    } else {
      spindleFactor = 1 + math.log10(math.pow(spindleValue / 40, 8.0 / 15.0));
    }

    return math.pow(holderFactor * fixtureFactor * spindleFactor, 1.0 / this.calculateMaterialStretch(material));
  };

  public setFeed = (
    fluteCount: number,
    chipLoad: number,
    speed: number,
    feedBar: number,
    feedFactor: number,
    maxIPM: number
  ): number => {
    let feedAggression = feedBar / 100;
    let feed = feedAggression * this.calculateInchesPerMinutes(speed, chipLoad * feedFactor, fluteCount);
    let ipmPrecision = math.pow(10, math.trunc(math.log10(feed))) / 100;

    return maxIPM > 0 && feed > maxIPM ? maxIPM : ipmPrecision * math.round(feed / ipmPrecision);
  };

  public setSpeed = (
    cutterDiameter: number,
    surfaceSpeed: number,
    machine: Machine_,
    speedParameters: Speed_,
    speedType: string
  ): number => {
    let speedAgression = speedParameters.speedBar / 100;
    let surfaceRPM = this.conversionsFormulas.convertSfmToRpm(surfaceSpeed, cutterDiameter);
    let speed = speedAgression * surfaceRPM;

    if (machine.maxRPM > 0 && speed > machine.maxRPM) {
      return machine.maxRPM;
    } else if (speedType == 'SPEED') {
      let rpmPrecision = 5;

      if (speed < 500) {
        rpmPrecision = 1;
      }

      return rpmPrecision * parseInt((speed / rpmPrecision).toFixed(0));
    }

    return speed;
  };

  public calculateChipThickness = (
    recommendation: Recommendation_,
    millingParameters: Parameters_,
    feedPerTooth: number,
    feedRate: number,
    feedFactor: number,
    tool: Cutter_
  ): Recommendation_ => {
    if (millingParameters.toolPath.operationName === '3D Surfacing') {
      recommendation.chipThickness = (feedPerTooth * millingParameters.cutterDiameter) / tool.cutterDiameter;
      recommendation.mrr = null;
    } else if (millingParameters.toolPath.operationName === 'Chamfering') {
      recommendation.chipThickness = undefined;
      recommendation.mrr = null;
    } else {
      recommendation.chipThickness = parseFloat((feedPerTooth / feedFactor).toString());
      recommendation.mrr = millingParameters.cutDepth * millingParameters.cutWidth * feedRate;
    }

    return recommendation;
  };

  public calculateRecommendation = (
    material: Material_,
    machine: Machine_,
    speedParameters: Speed_,
    millingParameters: Parameters_,
    tool: Cutter_,
    userHardness: number
  ): Recommendation_ => {
    let machiningValues = this.calculateMachining(
      material,
      tool,
      millingParameters,
      userHardness,
      this.getStandardMaterial(),
      this.getHEM(),
      this.getStandardTool()
    );
    let surfaceSpeed = machiningValues.surfaceSpeed * this.calculateMachineFactor('SPEED', material, machine);
    let chipLoad =
      (machiningValues.chipLoad *
        this.calculateMachineFactor('Feed', material, machine) *
        millingParameters.cutterDiameter) /
      tool.cutterDiameter;
    let spindleSpeed = this.setSpeed(millingParameters.cutterDiameter, surfaceSpeed, machine, speedParameters, 'SPEED');
    let feedRate = this.setFeed(
      tool.fluteCount,
      chipLoad,
      this.setSpeed(millingParameters.cutterDiameter, surfaceSpeed, machine, speedParameters, 'FEED'),
      speedParameters.feedBar,
      machiningValues.feedFactor,
      machine.maxIPM
    );
    let feedPerTooth = this.conversionsFormulas.convertIpmToIpt(spindleSpeed, feedRate, tool.fluteCount);

    let recommendation: Recommendation_ = {
      rpm: spindleSpeed,
      ipm: feedRate,
      sfm: this.conversionsFormulas.convertRpmToSfm(spindleSpeed, millingParameters.cutterDiameter),
      ipt: feedPerTooth
    };
    recommendation = this.calculateChipThickness(
      recommendation,
      millingParameters,
      feedPerTooth,
      feedRate,
      machiningValues.feedFactor,
      tool
    );

    if (millingParameters.toolPath.operationName === 'Trochoidal Slotting') {
      recommendation.ipt = null;
      recommendation.ipm = null;
    }

    return recommendation;
  };

  public calcultePlugingRecommendation = (
    material: Material_,
    machine: Machine_,
    speedParameters: Speed_,
    plungingParameters: Parameters_,
    tool: Cutter_,
    userHardness: number
  ): Recommendation_ => {
    let machiningValues = this.calculateMachining(material, tool, plungingParameters, userHardness);
    let surfaceSpeed = machiningValues.surfaceSpeed * this.calculateMachineFactor('SPEED', material, machine);
    let chipLoad = machiningValues.chipLoad * this.calculateMachineFactor('Feed', material, machine);
    let spindleSpeed = this.setSpeed(tool.cutterDiameter, surfaceSpeed, machine, speedParameters, 'SPEED');
    let feedRate = this.setFeed(
      tool.fluteCount,
      chipLoad,
      this.setSpeed(tool.cutterDiameter, surfaceSpeed, machine, speedParameters, 'FEED'),
      speedParameters.feedBar,
      1,
      machine.maxIPM
    );

    let pathDiameter = plungingParameters.holeDiameter - tool.cutterDiameter;
    let helixLength = math.sqrt(math.pow(math.PI * pathDiameter, 2) + math.pow(plungingParameters.pitch, 2));
    let pathLength = (helixLength * plungingParameters.holeDepth) / plungingParameters.pitch + math.PI * pathDiameter;
    let cutTime = pathLength / feedRate;
    let cutMinutes = parseInt(math.floor(cutTime).toFixed(0));
    let cutSeconds = parseInt(math.ceil(60 * (cutTime - cutMinutes)).toFixed(0));
    let cutVolume = math.PI * math.pow(plungingParameters.holeDiameter / 2, 2) * plungingParameters.holeDepth;

    let recommendation: Recommendation_ = {
      rpm: spindleSpeed,
      ipm: feedRate,
      sfm: this.conversionsFormulas.convertRpmToSfm(spindleSpeed, tool.cutterDiameter),
      ipt: this.conversionsFormulas.convertIpmToIpt(spindleSpeed, feedRate, tool.fluteCount),
      mrr: cutVolume / cutTime
    };

    let timeMinStr = cutMinutes.toString();
    let timeSecStr = cutSeconds.toString();

    if (cutSeconds < 10) {
      timeSecStr = '0' + timeSecStr;
    }

    recommendation.holeTime = timeMinStr + ':' + timeSecStr;

    return recommendation;
  };

  // endmill Formulas
  public resizeArray = (array: Array<any>, newSize: number): Array<any> => {
    let newArray: Array<any> = new Array(newSize);

    for (let i = 0; i < newSize && i < array.length; i++) {
      newArray.push(array[i]);
    }

    return newArray;
  };

  private constEndmillFeatures = (cutter: Cutter_): Cutter_ => {
    /*
            unfortunately this function, was defineEndmillFeatures, has to be kept and renamed to constEndmillFeatures,
            for calculating the standard tool and also tool for calculating MRR. The MRR does not use a standard tool,
            its a combination of standard and the tool selected
        */

    let emAttr: string[] = cutter.description.split('-');
    cutter.series = emAttr[0];
    cutter.taper = 0;
    cutter.coreDiameter = 0;

    //begin catalog logic
    if (cutter.description.indexOf('-C-RN-') > -1) {
      cutter.catalog = cutter.series + '-C-RN';
    } else if (cutter.description.indexOf('-C-') > -1) {
      cutter.catalog = cutter.series + '-C';
    } else if (cutter.description.indexOf('-RN-') > -1) {
      cutter.catalog = cutter.series + '-RN';
    } else {
      cutter.catalog = cutter.series;
    }

    if (cutter.series != 'HXF' && cutter.series.indexOf('CM') < 0) {
      cutter.catalog = cutter.catalog + '-' + cutter.fluteCount;
    }
    //end catalog logic

    //begin tool end logic
    if (cutter.series.indexOf('PCM') > -1) {
      cutter.end = cutter.series.replace(/HPCM/g, ' ') + '° Chamfer';
      cutter.taper = parseFloat(cutter.series.replace(/HPCM/g, '')) / 2;
    } else if (cutter.series.indexOf('CM') > -1) {
      cutter.end = cutter.series.replace(/HCM/g, ' ') + '° Chamfer';
      cutter.taper = parseFloat(cutter.series.replace(/HCM/g, '')) / 2;
    } else if (cutter.description.indexOf('BN') > -1) {
      cutter.end = 'Ball-Nosed';
    } else if (cutter.cornerRadius > 0) {
      cutter.end = 'Bull-Nosed';
    } else {
      cutter.end = 'Square Corner';
    }
    //end of tool end logic

    cutter.breaks = cutter.description.indexOf('-C-') > -1 || cutter.description.indexOf('R') > -1;

    let lengthCategory = cutter.description.replace(/-C-/g, '');
    lengthCategory = lengthCategory.replace(/RN/g, '');
    lengthCategory = lengthCategory.replace(cutter.series, '');
    lengthCategory = lengthCategory.replace(/-/g, '');
    cutter.lengthCategory = lengthCategory.substring(0, 1);

    let helixAngle: number = 0;
    let rakeAngle: number = 0;

    let start: number = 0;

    let end: number = this.ArrayMatlISO.length;
    let toolMaterials: Array<any> = new Array(end);

    switch (cutter.series) {
      case 'H35AL':
      case 'H40ALV':
      case 'H45AL':
        end = 8;
        break;
      case 'HST':
      case 'HSV':
      case 'HSVR':
        start = 4;
        break;
      case 'HEV':
      case 'HEF':
      case 'HSF':
      case 'HXF':
      case 'HXVR':
        start = 8;
        break;
    }

    toolMaterials = this.resizeArray(toolMaterials, end - start);

    for (let i = start; i < end; i++) {
      toolMaterials[i - start] = this.ArrayMatlISO[i];
    }

    // additional to old code, we are getting rid of the undefineds

    toolMaterials = _.filter(toolMaterials, toolMaterial => {
      if (toolMaterial) {
        return toolMaterial;
      }
    });

    cutter.materials = toolMaterials;

    switch (cutter.series) {
      case 'HEMV':
        cutter.coreDiameter = (62 * cutter.cutterDiameter) / 100;
        helixAngle = 40;
        rakeAngle = 7;
        break;
      case 'H35AL':
        cutter.coreDiameter = 0.5 * cutter.cutterDiameter;
        helixAngle = 35;
        rakeAngle = 12;
        break;
      case 'H40ALV':
        if (cutter.lengthCategory == 'S' || cutter.lengthCategory == 'SR') {
          cutter.coreDiameter = 0.45 * cutter.cutterDiameter;
        } else if (cutter.lengthCategory == 'R') {
          cutter.coreDiameter = 0.55 * cutter.cutterDiameter;
        } else {
          cutter.coreDiameter = 0.7 * cutter.cutterDiameter;
        }

        helixAngle = 40;
        rakeAngle = 12;
        break;
      case 'H45AL':
        if (cutter.fluteCount == 2) {
          cutter.coreDiameter = 0.44 * cutter.cutterDiameter;
        } else {
          cutter.coreDiameter = 0.5 * cutter.cutterDiameter;
        }

        helixAngle = 45;
        rakeAngle = 12;
        break;
      case 'HST':
        cutter.coreDiameter = 0.55 * cutter.cutterDiameter;
        helixAngle = 35;
        rakeAngle = 7;
        break;
      case 'HSV':
        cutter.coreDiameter = 0.5 * cutter.cutterDiameter;
        helixAngle = 35;
        rakeAngle = 7;
        break;
      case 'HSVR':
        cutter.coreDiameter = 0.55 * cutter.cutterDiameter;
        helixAngle = 37;
        rakeAngle = 6;
        break;
      case 'HEV':
        if (cutter.fluteCount == 5) {
          cutter.coreDiameter = 0.62 * cutter.cutterDiameter;
          rakeAngle = 7;
          helixAngle = 40;
        } else if (cutter.fluteCount == 6) {
          if (cutter.lengthCategory == 'S' || cutter.lengthCategory == 'SR') {
            cutter.coreDiameter = 0.62 * cutter.cutterDiameter;
          } else {
            cutter.coreDiameter = 0.67 * cutter.cutterDiameter;
          }

          rakeAngle = 8;
          helixAngle = 37;
        } else {
          if (cutter.lengthCategory == 'S' || cutter.lengthCategory == 'SR') {
            cutter.coreDiameter = 0.65 * cutter.cutterDiameter;
          } else {
            cutter.coreDiameter = 0.75 * cutter.cutterDiameter;
          }

          rakeAngle = 5;
          helixAngle = 38;
        }

        break;
      case 'HEF':
        cutter.coreDiameter = 0.62 * cutter.cutterDiameter;
        helixAngle = 45;
        rakeAngle = 7;
        break;
      case 'HSF':
        cutter.coreDiameter = 0.65 * cutter.cutterDiameter;
        helixAngle = 45;
        rakeAngle = 7;
        break;
      case 'HXF':
        cutter.coreDiameter = 0.75 * cutter.cutterDiameter;
        helixAngle = 40;
        rakeAngle = 7;
        break;
      case 'HXVR':
        if (cutter.fluteCount == 4) {
          cutter.coreDiameter = 0.5 * cutter.cutterDiameter;
          helixAngle = 43.5;
          rakeAngle = 15;
        } else {
          cutter.coreDiameter = 0.6 * cutter.cutterDiameter;
          helixAngle = 40;
          rakeAngle = 10;
        }

        break;
      case 'HPCM60':
        helixAngle = 30;
        break;
      case 'HPCM90':
        helixAngle = 15;
        break;
      case 'HPCM120':
        helixAngle = 15;
        break;
    }

    cutter.helix = (math.PI * helixAngle) / 180;
    cutter.rake = (math.PI * rakeAngle) / 180;

    let endmillFluteWash =
      (math.sqrt(math.pow(cutter.cutterDiameter, 2) - math.pow(cutter.coreDiameter, 2)) * 88) /
      (21 * math.pow(cutter.fluteCount, math.sqrt(2)));

    cutter.fluteWash = endmillFluteWash * math.cos(cutter.helix);

    cutter.fluteCount = typeof cutter.fluteCount === 'string' ? parseFloat(cutter.fluteCount) : cutter.fluteCount;

    return cutter;
  };

  public defineEndmillFeatures = (cutter: Cutter_): Cutter_ => {
    let emAttr: string[] = cutter.description.split('-');
    cutter.series = emAttr[0];

    //begin catalog logic
    if (cutter.description.indexOf('-C-RN-') > -1) {
      cutter.catalog = cutter.series + '-C-RN';
    } else if (cutter.description.indexOf('-C-') > -1) {
      cutter.catalog = cutter.series + '-C';
    } else if (cutter.description.indexOf('-RN-') > -1) {
      cutter.catalog = cutter.series + '-RN';
    } else {
      cutter.catalog = cutter.series;
    }

    if (cutter.series != 'HXF' && cutter.series.indexOf('CM') < 0) {
      cutter.catalog = cutter.catalog + '-' + cutter.fluteCount;
    }
    //end catalog logic

    //begin tool end logic
    if (cutter.series.indexOf('PCM') > -1) {
      cutter.end = cutter.series.replace(/HPCM/g, ' ') + '° Chamfer';
    } else if (cutter.series.indexOf('CM') > -1) {
      cutter.end = cutter.series.replace(/HCM/g, ' ') + '° Chamfer';
    } else if (cutter.description.indexOf('BN') > -1) {
      cutter.end = 'Ball-Nosed';
    } else if (cutter.cornerRadius > 0) {
      cutter.end = 'Bull-Nosed';
    } else {
      cutter.end = 'Square Corner';
    }
    //end of tool end logic

    //begin lengthCategory logic
    let lengthCategory = cutter.description.replace(/-C-/g, '');
    lengthCategory = lengthCategory.replace(/RN/g, '');
    lengthCategory = lengthCategory.replace(cutter.series, '');
    lengthCategory = lengthCategory.replace(/-/g, '');
    cutter.lengthCategory = lengthCategory.substring(0, 1);
    //end lengthCategory logic

    //begin tool supported material logic
    let start: number = 0;

    let end: number = this.ArrayMatlISO.length;
    let toolMaterials: Array<any> = new Array(end);

    switch (cutter.series) {
      case 'H35AL':
      case 'H40ALV':
      case 'H45AL':
        end = 8;
        break;
      case 'HST':
      case 'HSV':
      case 'HSVR':
        start = 4;
        break;
      case 'HEV':
      case 'HEF':
      case 'HSF':
      case 'HXF':
      case 'HXVR':
        start = 8;
        break;
    }

    toolMaterials = this.resizeArray(toolMaterials, end - start);

    for (let i = start; i < end; i++) {
      toolMaterials[i - start] = this.ArrayMatlISO[i];
    }

    // additional to old code, we are getting rid of the undefineds
    toolMaterials = _.filter(toolMaterials, toolMaterial => {
      if (toolMaterial) {
        return toolMaterial;
      }
    });

    cutter.materials = toolMaterials;
    //end tool supported material logic

    //begin flutewash logic
    let endmillFluteWash =
      (math.sqrt(math.pow(cutter.cutterDiameter, 2) - math.pow(cutter.coreDiameter, 2)) * 88) /
      (21 * math.pow(cutter.fluteCount, math.sqrt(2)));

    cutter.fluteWash = endmillFluteWash * math.cos(cutter.helix);

    cutter.fluteCount = typeof cutter.fluteCount === 'string' ? parseFloat(cutter.fluteCount) : cutter.fluteCount;
    //end flutewash logic

    if (typeof cutter.pitch !== 'string') {
      cutter.pitch = cutter.pitch ? 'even' : 'variable';
    }

    cutter.taper = typeof cutter.anglePerSide === 'string' ? parseFloat(cutter.anglePerSide) : cutter.anglePerSide;

    return cutter;
  };

  //material formulas
  public configureMaterial = (material: Material_): Material_ => {
    if (!material.hardType) {
      material.hardType = material['hardnessFactor'];
    }

    switch (material.hardType) {
      case 'HBW':
        material.hra = this.conversionsFormulas.convertHBWtoHRA(material.hardness);
        break;
      case 'HBS':
        material.hra = this.conversionsFormulas.convertHBStoHRA(material.hardness);
        break;
      case 'HRC':
        material.hra = this.conversionsFormulas.convertHRCtoHRA(material.hardness);
        break;
      case 'HRB':
        material.hra = this.conversionsFormulas.convertHRBtoHRA(material.hardness);
        break;
    }

    material.plasticity = 1 + material.elongation;
    material.shearFactor = 1 / (5 * math.sqrt(material.poissons - 197.0 / 900.0));

    if (material.iso !== '') {
      if (material['elasticitiy']) {
        material.elasticity = material['elasticitiy'];
      }
    }

    return material;
  };

  public calculateSfmRatio = (
    recommendations: RecommendationLabel[],
    holderFactor: number,
    spindleFactor: number,
    maxRPM: number,
    speedAggression: number,
    feedAggression: number
  ) => {
    const sfmField = recommendations.find(rec => rec.recommendationLabel === 'Spindle Speed');
    if (sfmField) {
      const adjustedSfmValue =
        Number(sfmField.recommendationValue) *
        (sfmField.recommendationAdjustment.includes('SFM') ? holderFactor * speedAggression : 1) *
        (sfmField.recommendationAdjustment.includes('FPT') ? spindleFactor * feedAggression : 1);

      if (maxRPM && adjustedSfmValue > maxRPM) {
        return maxRPM / adjustedSfmValue;
      } else {
        return 1;
      }
    } else {
      return 1;
    }
  };

  public calculateFptRatio = (
    recommendations: RecommendationLabel[],
    holderFactor: number,
    spindleFactor: number,
    maxIPM: number,
    speedAggression: number,
    feedAggression: number,
    sfmRation: number
  ) => {
    const fptField = recommendations.find(rec => rec.recommendationLabel === 'Feed Per Minute');
    if (fptField) {
      const adjustedFptValue =
        Number(fptField.recommendationValue) *
        (fptField.recommendationAdjustment.includes('SFM') ? holderFactor * speedAggression : 1) *
        (fptField.recommendationAdjustment.includes('FPT') ? spindleFactor * feedAggression : 1) *
        sfmRation;

      if (maxIPM && adjustedFptValue > maxIPM) {
        return maxIPM / adjustedFptValue;
      } else {
        return 1;
      }
    } else {
      return 1;
    }
  };
}
